/**
 * Redux witness watchers for iamges
 * @module services/images/watchers
 */
import { Map, List } from 'immutable'
import { delay, map as mapPromise } from 'bluebird'
import { getLocationPathname } from '../location'
import {
  GET_DATA_LIST,
  setDataList as setDataListAction,
  resetData as resetDataAction,
  setDataList,
  setDataApplication,
  setDataApplicationByKey,
} from '../data/redux/actions'
import {
  CREATE_IMAGES,
  CREATE_IMAGE,
  DELETE_IMAGE,
  DROP_IMAGES,
  showBulkUploadSupportError as showBulkUploadSupportErrorAction,
  setBulkUploadBatchSummary as setBulkUploadBatchSummaryAction,
} from './redux/actions'
import {
  renderModal as renderModalAction,
  dismissModal as dismissModalAction,
} from '../modal/redux/actions'
import { FETCHING_MODAL } from '../modal'
import { getConfigDataServer } from '../config'
import { createViewRecords, getViewRecords, getApplication } from '../data'
import { getContentList, isContentDetailPage } from '../content'
import { getContentReferencesList, filterByContentReferenceType } from '../content-references'
import { getContentTypes, getD6ContentType } from '../content/d6-content-types'
import { CONTENT_REFERENCE_TYPE_SERIES } from '../content-references/types'
import { getExponentialDelay } from '../../exponential-backoff'
import { showErrorToast, showSuccessToast } from '../toast/redux/actions'
import {
  getFirstErrorMessage,
  getErrorStatus,
  isRetryableRequestError,
  UNHANDLED_ERROR,
} from '../error'
import {
  expandDroppedMatrixMasters,
  syncMatrixDerivedConfirmRows,
} from '../../gui/components/images-upload-bulk-page/bulk-matrix-expand'
import {
  normalizeSlotFillMode,
  normalizeSquareSource,
} from '../../gui/components/images-upload-bulk-page/closest-slot-matrix'
import {
  getImageContentRelationshipList,
  getImageContentRelationshipByD6IdAndD6ContentType,
  createImageContentRelationshipUnique,
  createImage,
  deleteImage,
  placeImageFromSource,
  isValidImageContentRelationship,
} from '.'
import { placeFromSourceSlotErrors } from './place-from-source-errors'
import { STORE_KEY_IMAGES } from './store-keys'
import * as readBlobAsDataUrlModule from './read-blob-as-data-url'
import * as bulkUploadIncident from './bulk-upload-incident'

/**
 * Browsers cap concurrent connections per host at around six, so a wider batch
 * only queues large multipart uploads and gives them more time to be aborted
 */
const UPLOAD_CONCURRENCY = 4

const MAX_UPLOAD_ATTEMPTS = 3

/**
 * Prefer the live Redux fill mode so a radio change during drop
 * is not overwritten by the DROP_IMAGES payload snapshot.
 */
function latestSlotFillMode (getState, fallbackApplication) {
  if (typeof getState === 'function') {
    const reduxState = getState()
    const data = reduxState && reduxState.data
    if (Map.isMap(data)) {
      const latest = getApplication({ data, storeKey: STORE_KEY_IMAGES })
      if (latest) {
        return normalizeSlotFillMode(latest.get('slotFillMode'))
      }
    }
  }
  return normalizeSlotFillMode(fallbackApplication.get('slotFillMode'))
}

function latestSquareSource (getState, fallbackApplication) {
  if (typeof getState === 'function') {
    const reduxState = getState()
    const data = reduxState && reduxState.data
    if (Map.isMap(data)) {
      const latest = getApplication({ data, storeKey: STORE_KEY_IMAGES })
      if (latest) {
        return normalizeSquareSource(latest.get('squareSource'), { omittedMeans16x9: false })
      }
    }
  }
  return normalizeSquareSource(fallbackApplication.get('squareSource'), { omittedMeans16x9: false })
}

/**
 * Dispatch a pasteable support error, log it and record it in New Relic
 * @param {Function} dispatch
 * @param {Object} report
 * @param {Error} [error] The originating error
 */
function reportBulkUploadSupportError (dispatch, report, error) {
  bulkUploadIncident.logBulkUploadIncident(report)
  bulkUploadIncident.emitBulkUploadIncident(report, error)
  dispatch(showBulkUploadSupportErrorAction({ report }))
}

/**
 * Run a request, retrying only failures that could succeed on another try.
 * Validation and auth failures throw immediately instead of burning attempts.
 * @param {Function} request Returns the request promise
 * @param {Object} [param1] Options
 * @param {Number} [param1.maxAttempt] Total attempts allowed
 * @param {Number} [param1.attempt] The current zero based attempt
 * @param {Function} [param1.onAttempt] Called with the attempt count as it grows
 */
async function withTransientRetry (
  request,
  {
    maxAttempt = MAX_UPLOAD_ATTEMPTS,
    attempt = 0,
    onAttempt,
    shouldRetry = isRetryableRequestError,
  } = {},
) {
  if (onAttempt) {
    onAttempt(attempt + 1)
  }
  try {
    return await request()
  } catch (e) {
    if (attempt + 1 < maxAttempt && shouldRetry(e)) {
      await delay(getExponentialDelay(attempt))
      return withTransientRetry(request, {
        maxAttempt,
        attempt: attempt + 1,
        onAttempt,
        shouldRetry,
      })
    }
    throw e
  }
}

/**
 * Watcher to listen for the set data list action on image upload bulk page and load dependancies
 */
export function onImageUploadBulkGetDataList ({ after }) {
  return after(DROP_IMAGES, async ({ state, action, dispatch, getState }) => {
    const { auth, config } = state
    const {
      payload: { options, application },
    } = action

    const files = options.get('files', [])
    let applicationData = application.set('matrixExpanding', true)

    const commitApplication = (nextApplication) => {
      applicationData = nextApplication.set(
        'slotFillMode',
        latestSlotFillMode(getState, nextApplication),
      )
      dispatch(
        setDataApplication({
          storeKey: STORE_KEY_IMAGES,
          application: applicationData,
        }),
      )
    }

    commitApplication(applicationData)
    const batchId = bulkUploadIncident.createBulkUploadBatchId()
    const batchSize = files.length
    const batchStartedAt = Date.now()

    /**
     * Record a failed catalog lookup in New Relic. These do not stop the upload,
     * so they stay out of the support snackbar and keep the existing toast.
     * @param {Error} error The lookup error
     * @param {Object} param1 The image being prepared and the endpoint that failed
     */
    const reportContentLookupFailure = (error, { fileName, itemId, endpoint } = {}) => {
      const brooklynDetail = getFirstErrorMessage(error)
      bulkUploadIncident.emitBulkUploadIncident(
        bulkUploadIncident.createBulkUploadIncidentReport({
          batchId,
          fileName,
          itemId,
          stage: bulkUploadIncident.BULK_UPLOAD_STAGE_CONTENT_LOOKUP,
          httpStatus: getErrorStatus(error),
          errorName: error.name,
          errorCode: error.code,
          brooklynDetail,
          message: `${endpoint} lookup failed`,
          rawMessage: error.message,
          batchSize,
          elapsedMs: Date.now() - batchStartedAt,
        }),
        error,
      )
      dispatch(showErrorToast(brooklynDetail))
    }

    await mapPromise(
      files,
      async (imageData) => {
        const contentId = imageData.getIn(['contentRelationship', 'd6Id'])
        const possibleContentTypes = getContentTypes(
          imageData.getIn(['contentRelationship', 'd6ContentType']),
        )
        const possibleContent = possibleContentTypes.map((contentType) =>
          Map({ contentId, contentType }),
        )
        const language = imageData.getIn(['contentRelationship', 'language'])
        const imageId = imageData.get('id')
        const fileBlob = imageData.get('fileBlob')
        const originalFileName = imageData.get('originalFileName')
        let isSource = imageData.getIn(['contentRelationship', 'isSource'])
        let dataUri
        let attemptsMade = 1

        try {
          dataUri = await withTransientRetry(
            () => readBlobAsDataUrlModule.readBlobAsDataUrl(fileBlob),
            {
              shouldRetry: readBlobAsDataUrlModule.isRetryableFileReadError,
              onAttempt: (attempts) => {
                attemptsMade = attempts
              },
            },
          )
        } catch (e) {
          const fileReaderDetail = e.fileReaderErrorMessage
            ? `${e.message} (${e.fileReaderErrorMessage})`
            : e.message
          const report = bulkUploadIncident.createBulkUploadIncidentReport({
            batchId,
            fileName: originalFileName,
            itemId: imageId,
            stage: bulkUploadIncident.BULK_UPLOAD_STAGE_FILE_READ,
            urlPresent: false,
            urlLength: 0,
            errorName: e.fileReaderErrorName || e.name,
            errorCode: e.fileReaderErrorName || e.code || null,
            fileSize: e.fileSize,
            fileType: e.fileType,
            message: e.message || 'Failed to read image file bytes',
            rawMessage: fileReaderDetail,
            batchSize,
            attemptsMade,
            elapsedMs: Date.now() - batchStartedAt,
          })
          reportBulkUploadSupportError(dispatch, report, e)
          commitApplication(
            applicationData
              .setIn(['bulkUpload', imageId, 'error'], report.message)
              .setIn(['bulkUpload', imageId, 'compressing'], false),
          )
          return
        }

        if (attemptsMade > 1) {
          bulkUploadIncident.emitBulkUploadFileReadRecovered(
            bulkUploadIncident.createBulkUploadFileReadRecoveredReport({
              batchId,
              fileName: originalFileName,
              itemId: imageId,
              batchSize,
              attemptsMade,
              fileSize: fileBlob?.size,
              fileType: fileBlob?.type,
              elapsedMs: Date.now() - batchStartedAt,
            }),
          )
        }

        // Check content information, but don't fail if not found
        let contentList = Map()
        try {
          contentList = await getContentList({
            auth,
            server: getConfigDataServer(config),
            content: possibleContent.toJS(),
            language,
          })
        } catch (e) {
          reportContentLookupFailure(e, {
            fileName: originalFileName,
            itemId: imageId,
            endpoint: 'content-list',
          })
        }

        // Check content information, but don't fail if not found
        let contentReferencesList = Map()
        try {
          if (!contentList.get('content', List()).isEmpty()) {
            contentReferencesList = await getContentReferencesList({
              auth,
              server: getConfigDataServer(config),
              contentReferences: contentList
                .get('content', List())
                .map((v) =>
                  Map({
                    fromContentContentId: v.get('contentId'),
                    fromContentContentType: v.get('contentType'),
                  }),
                )
                .toJS(),
              language,
            })
          }
        } catch (e) {
          reportContentLookupFailure(e, {
            fileName: originalFileName,
            itemId: imageId,
            endpoint: 'content-reference-list',
          })
        }

        try {
          const seriesContentReferences = filterByContentReferenceType(
            contentReferencesList.get('contentReferences', List()),
            List([CONTENT_REFERENCE_TYPE_SERIES]),
          )
          // Check content information, but don't fail if not found
          if (!seriesContentReferences.isEmpty()) {
            const contentSeriesList = await getContentList({
              auth,
              server: getConfigDataServer(config),
              content: seriesContentReferences
                .map((v) => ({
                  contentId: v.get('toContentContentId'),
                  contentType: v.get('toContentContentType'),
                }))
                .toJS(),
              language,
            })
            contentList = contentList.merge(
              Map({
                totalCount: contentList.get('totalCount', 0) + 1,
                content: contentList
                  .get('content', List())
                  .push(contentSeriesList.get('content', List()).first()),
              }),
            )
          }
        } catch (e) {
          reportContentLookupFailure(e, {
            fileName: originalFileName,
            itemId: imageId,
            endpoint: 'content-list-series',
          })
        }

        const firstContentItem = contentList.get('content', List()).first()

        if (firstContentItem) {
          isSource = firstContentItem.get('primaryLanguage') === language
        }

        const contentRelationship = createImageContentRelationshipUnique(
          Map({
            d6Id: imageData.getIn(['contentRelationship', 'd6Id']),
            d6ContentType: imageData.getIn(['contentRelationship', 'd6ContentType']),
            type: imageData.getIn(['contentRelationship', 'type']),
            language,
            isSource,
          }),
        )

        // If we don't have a valid content relationship don't get dependancies
        if (!isValidImageContentRelationship(contentRelationship)) {
          return
        }

        dispatch(
          setDataList({
            storeKey: 'content',
            options: Map({ image: Map({ contentRelationship }) }),
            processing: true,
          }),
        )
        // Set the image content references
        dispatch(
          setDataList({
            storeKey: 'contentReferences',
            options: Map({ image: Map({ contentRelationship }) }),
            processing: true,
          }),
        )
        // Set the image content relationships
        dispatch(
          setDataList({
            storeKey: STORE_KEY_IMAGES,
            options: contentRelationship,
            processing: true,
          }),
        )

        let imageList = List()
        try {
          imageList = await getImageContentRelationshipList({
            auth,
            server: getConfigDataServer(config),
            image: Map({ contentRelationship }),
          })
        } catch (e) {
          reportContentLookupFailure(e, {
            fileName: originalFileName,
            itemId: imageId,
            endpoint: 'image-content-relationship-list',
          })
        }

        // Set the image content
        dispatch(
          setDataList({
            storeKey: 'content',
            options: Map({ image: Map({ contentRelationship }) }),
            total: contentList.get('totalCount') || 0,
            records: createViewRecords(contentList.get('content') || List()),
            processing: false,
          }),
        )
        // Set the image content references
        dispatch(
          setDataList({
            storeKey: 'contentReferences',
            options: Map({ image: Map({ contentRelationship }) }),
            total: contentReferencesList.get('totalCount') || 0,
            records: createViewRecords(contentReferencesList.get('contentReferences') || List()),
            processing: false,
          }),
        )
        // Set the image content relationships
        dispatch(
          setDataList({
            storeKey: STORE_KEY_IMAGES,
            options: contentRelationship,
            total: imageList.size,
            records: createViewRecords(imageList),
            processing: false,
          }),
        )

        commitApplication(
          applicationData
            .setIn(['bulkUpload', imageId, 'data', 'contentRelationship'], contentRelationship)
            .setIn(['bulkUpload', imageId, 'data', 'url'], dataUri)
            .setIn(['bulkUpload', imageId, 'compressing'], false)
            .setIn(
              ['bulkUpload', imageId, 'contentType'],
              firstContentItem ? firstContentItem.get('contentType') : undefined,
            ),
        )
      },
      { concurrency: UPLOAD_CONCURRENCY },
    )

    const server = getConfigDataServer(config)
    try {
      const slotFillMode = latestSlotFillMode(getState, applicationData)
      const squareSource = latestSquareSource(getState, applicationData)
      applicationData = applicationData
        .set('slotFillMode', slotFillMode)
        .set('squareSource', squareSource)
      applicationData = await expandDroppedMatrixMasters({
        applicationData,
        auth,
        server,
        createImage,
        deleteImage,
        slotFillMode,
        squareSource,
        getLiveImages: ({ d6Id, d6ContentType, language }) =>
          getImageContentRelationshipByD6IdAndD6ContentType({
            auth,
            server,
            d6Id,
            d6ContentType,
            language,
          }),
      })
      const latestMode = latestSlotFillMode(getState, applicationData)
      const latestSquare = latestSquareSource(getState, applicationData)
      applicationData = syncMatrixDerivedConfirmRows(
        applicationData.set('slotFillMode', latestMode).set('squareSource', latestSquare),
        { slotFillMode: latestMode, squareSource: latestSquare, server },
      )
    } finally {
      commitApplication(applicationData.set('matrixExpanding', false))
    }
  }).when(({ state, action }) => {
    const { payload: { storeKey } = {} } = action
    const { location = Map() } = state
    return storeKey === STORE_KEY_IMAGES && getLocationPathname(location) === '/images/upload-bulk'
  })
}

/**
 * Watcher to listen for the set data list action on image by content page and load dependancies
 */
export function onImageByContentGetDataList ({ after }) {
  return after(GET_DATA_LIST, async ({ state, action, dispatch }) => {
    const { auth, config } = state
    const {
      payload: { options },
    } = action
    const contentId = options.get('contentId')
    const contentType = options.get('contentType')
    dispatch(
      setDataList({
        storeKey: 'content',
        options,
        processing: true,
      }),
    )
    // Set the image content references
    dispatch(
      setDataList({
        storeKey: 'contentReferences',
        options,
        processing: true,
      }),
    )
    // Set the image content relationships
    dispatch(
      setDataList({
        storeKey: STORE_KEY_IMAGES,
        options,
        processing: true,
      }),
    )
    let contentList = Map()
    let contentError = null
    try {
      // Check content information, but don't fail if not found
      contentList = await getContentList({
        auth,
        server: getConfigDataServer(config),
        content: [{ contentId, contentType }],
      })
    } catch (e) {
      contentError = e
    }
    let contentReferencesList = Map()
    const contentReferencesError = null
    try {
      // Check content information, but don't fail if not found
      if (!contentList.get('content', List()).isEmpty()) {
        contentReferencesList = await getContentReferencesList({
          auth,
          server: getConfigDataServer(config),
          contentReferences: [
            {
              fromContentContentId: contentId,
              fromContentContentType: contentType,
            },
          ],
        })
      }
    } catch (e) {
      // Do nothing
    }
    try {
      const seriesContentReferences = filterByContentReferenceType(
        contentReferencesList.get('contentReferences', List()),
        List([CONTENT_REFERENCE_TYPE_SERIES]),
      )
      // Check content information, but don't fail if not found
      if (!seriesContentReferences.isEmpty()) {
        const contentSeriesList = await getContentList({
          auth,
          server: getConfigDataServer(config),
          content: seriesContentReferences
            .map((v) => ({
              contentId: v.get('toContentContentId'),
              contentType: v.get('toContentContentType'),
            }))
            .toJS(),
        })
        contentList = contentList.merge(
          Map({
            totalCount: contentList.get('totalCount', 0) + 1,
            content: contentList
              .get('content', List())
              .push(contentSeriesList.get('content', List()).first()),
          }),
        )
      }
    } catch (e) {
      // Do nothing
    }
    let imageList = List()
    let imageError = null
    try {
      imageList = await getImageContentRelationshipList({
        auth,
        server: getConfigDataServer(config),
        image: Map({
          contentRelationship: Map({
            d6Id: contentId,
            d6ContentType: getD6ContentType(contentType),
          }),
        }),
      })
    } catch (e) {
      imageError = e
    }
    // Set the image content
    dispatch(
      setDataList({
        storeKey: 'content',
        options,
        error: contentError,
        total: contentList.get('totalCount') || 0,
        records: createViewRecords(contentList.get('content') || List()),
        processing: false,
      }),
    )
    // Set the image content references
    dispatch(
      setDataList({
        storeKey: 'contentReferences',
        options,
        error: contentReferencesError,
        total: contentReferencesList.get('totalCount') || 0,
        records: createViewRecords(contentReferencesList.get('contentReferences') || List()),
        processing: false,
      }),
    )
    // Set the image content relationships
    dispatch(
      setDataList({
        storeKey: STORE_KEY_IMAGES,
        options,
        error: imageError,
        total: imageList.size,
        records: createViewRecords(imageList),
        processing: false,
      }),
    )
  }).when(({ state, action }) => {
    const { payload: { storeKey } = {} } = action
    const { location = Map() } = state
    return (
      storeKey === STORE_KEY_IMAGES && getLocationPathname(location) === '/images/manage-by-content'
    )
  })
}

export function getImages ({ after }) {
  return after(GET_DATA_LIST, async ({ action, state, dispatch }) => {
    const { auth, config, data } = state
    const { storeKey, options } = action.payload
    const images = data.get('images')
    if (images) {
      dispatch(resetDataAction({ storeKey }))
    }
    dispatch(
      setDataListAction({
        storeKey,
        options,
        processing: true,
      }),
    )
    let imageContentRelationships = []
    try {
      imageContentRelationships = await getImageContentRelationshipByD6IdAndD6ContentType({
        auth,
        server: getConfigDataServer(config),
        d6Id: options.get('contentId'),
        d6ContentType: getD6ContentType(options.get('contentType')),
        language: options.get('language'),
      })
    } catch (e) {
      // do nothing
    }
    dispatch(
      setDataListAction({
        storeKey,
        options,
        records: createViewRecords(imageContentRelationships),
        processing: false,
      }),
    )
  }).when(({ action, state }) => isImageDataAction({ action }) && isContentDetailPage(state))
}

/**
 * Returns true if the action has a storeKey of 'images'
 * @param {Object} param0 The parameters
 * @param {Object} [param0.action={}] A redux action
 * @param {Object} [param0.action.payload] The action payload
 * @param {String} [param0.action.payload.storeKey] The store key
 * @returns {Boolean} True when store key equals images
 */
function isImageDataAction ({ action = {} } = {}) {
  const { payload: { storeKey } = {} } = action
  return storeKey === STORE_KEY_IMAGES
}

/**
 * Create images in the provide by the action application property using the application key
 * @param {Object} param0 The params
 */
export function onCreateImages ({ after }) {
  return after(CREATE_IMAGES, async ({ action, dispatch, state }) => {
    const { config, auth } = state
    const { payload: { storeKey, applicationKey } = {} } = action
    let { payload: { application = Map() } = {} } = action
    const batchId = bulkUploadIncident.createBulkUploadBatchId()
    const imagesMap = application.get(applicationKey, Map())
    const batchSize = imagesMap.size
    const batchStartedAt = Date.now()
    let attempted = 0
    let succeeded = 0
    let failed = 0
    let skipped = 0
    application = application.withMutations((mutatorItem) =>
      mutatorItem.updateIn([applicationKey], Map(), (updaterItem) =>
        updaterItem.map((v) => {
          if (v.get('isMatrixDerived')) {
            return v.set('creating', false).set('error', null)
          }
          return v.set('creating', true).set('error', null)
        }),
      ),
    )
    dispatch(setDataApplication({ storeKey, application }))

    dispatch(
      renderModalAction(FETCHING_MODAL, {
        innerClass: 'modal__fetching',
        backgroundDismiss: false,
        hideDismiss: true,
        title: 'Uploading images',
      }),
    )

    /**
     * SET_DATA_APPLICATION_BY_KEY replaces the whole bulk map. Apply each
     * completion to the latest local map so concurrent uploads cannot restore
     * rows a faster sibling already removed.
     */
    const commitBulkMap = (updater) => {
      application = application.update(applicationKey, Map(), updater)
      dispatch(
        setDataApplicationByKey({
          storeKey,
          applicationKey,
          applicationData: application.get(applicationKey),
        }),
      )
    }

    // Create [key, value] tuples for bluebird to map over
    const imagesTuples = application.get(applicationKey, Map()).entries()
    await mapPromise(
      imagesTuples,
      async ([imageApplicationKey, imageApplicationData] = []) => {
        const originalFileName = imageApplicationData.getIn(['data', 'originalFileName'])
        const imageUrl = imageApplicationData.getIn(['data', 'url'])
        let attemptsMade = 1

        const failImage = (report, error) => {
          failed += 1
          reportBulkUploadSupportError(dispatch, report, error)
          commitBulkMap((bulk) =>
            bulk.update(imageApplicationKey, (value) =>
              value.set('creating', false).set('error', report.message),
            ),
          )
        }

        try {
          const contentRelationship = imageApplicationData.getIn(
            ['data', 'contentRelationship'],
            Map(),
          )

          // Only create the image if we don't have a content relationship or it is valid
          if (!isValidImageContentRelationship(contentRelationship)) {
            skipped += 1
            return
          }

          if (imageApplicationData.get('isMatrixDerived')) {
            skipped += 1
            return
          }

          if (!imageUrl && !imageApplicationData.get('isMatrixSource')) {
            failImage(
              bulkUploadIncident.createBulkUploadIncidentReport({
                batchId,
                fileName: originalFileName,
                itemId: imageApplicationKey,
                stage: bulkUploadIncident.BULK_UPLOAD_STAGE_CREATE,
                urlPresent: false,
                urlLength: 0,
                message: 'Image bytes were not ready before upload',
                batchSize,
                elapsedMs: Date.now() - batchStartedAt,
              }),
            )
            return
          }

          attempted += 1
          const imageContentRelationship = await withTransientRetry(
            () => {
              if (imageApplicationData.get('isMatrixSource')) {
                return placeImageFromSource({
                  auth,
                  server: getConfigDataServer(config),
                  sourceImageId: imageApplicationData.get('sourceImageId'),
                  primaryType:
                    imageApplicationData.get('primaryType') || contentRelationship.get('type'),
                  language: contentRelationship.get('language'),
                  d6Id: contentRelationship.get('d6Id'),
                  d6ContentType: contentRelationship.get('d6ContentType'),
                  skipTypes: imageApplicationData.get('skipTypes') || [],
                  seeds: imageApplicationData.get('seeds') || {},
                  contentType: imageApplicationData.get('contentType'),
                  squareSource: application.get('squareSource'),
                  isSource: contentRelationship.get('isSource'),
                }).then((placed) => {
                  const slotErrors = placeFromSourceSlotErrors(placed)
                  if (slotErrors.length) {
                    const err = new Error(slotErrors.join('; '))
                    err.placeFromSourceErrors = slotErrors
                    throw err
                  }
                  return placed
                })
              }
              return createImage({
                image: imageApplicationData.get('data'),
                auth,
                server: getConfigDataServer(config),
                processImage: true,
              })
            },
            {
              onAttempt: (attempts) => {
                attemptsMade = attempts
              },
              shouldRetry: (e) => !e.placeFromSourceErrors && isRetryableRequestError(e),
            },
          )

          if (!imageApplicationData.get('isMatrixSource')) {
            dispatch(
              setDataList({
                storeKey: STORE_KEY_IMAGES,
                options: createImageContentRelationshipUnique(contentRelationship),
                error: null,
                total: 1,
                records: createViewRecords(List([imageContentRelationship])),
              }),
            )
          }

          commitBulkMap((bulk) =>
            bulk
              .delete(imageApplicationKey)
              .filter((item) => item.get('parentItemId') !== imageApplicationKey),
          )

          succeeded += 1
          dispatch(showSuccessToast(`${originalFileName} was successfully uploaded`))
        } catch (e) {
          const brooklynDetail = (() => {
            const fromBody = getFirstErrorMessage(e)
            if (fromBody && fromBody.trim() !== UNHANDLED_ERROR) {
              return fromBody
            }
            return e.message || fromBody
          })()
          failImage(
            bulkUploadIncident.createBulkUploadIncidentReport({
              batchId,
              fileName: originalFileName,
              itemId: imageApplicationKey,
              stage: bulkUploadIncident.BULK_UPLOAD_STAGE_CREATE,
              urlPresent: Boolean(imageUrl),
              urlLength: typeof imageUrl === 'string' ? imageUrl.length : 0,
              httpStatus: getErrorStatus(e),
              errorName: e.name,
              errorCode: e.code,
              brooklynDetail,
              message: brooklynDetail || e.message || 'Image upload failed',
              rawMessage: e.message,
              batchSize,
              attemptsMade,
              elapsedMs: Date.now() - batchStartedAt,
            }),
            e,
          )
        }
      },
      { concurrency: UPLOAD_CONCURRENCY },
    )

    const summary = bulkUploadIncident.createBulkUploadBatchSummary({
      batchId,
      queued: batchSize,
      attempted,
      succeeded,
      failed,
      skipped,
      elapsedMs: Date.now() - batchStartedAt,
    })
    bulkUploadIncident.emitBulkUploadBatchSummary(summary)
    dispatch(setBulkUploadBatchSummaryAction({ summary }))

    dispatch(dismissModalAction())
  })
}

/**
 * Delete an image, retrying transient failures
 * @param {Object} config The config state
 * @param {Map} auth The auth state
 * @param {String} imageId The image id to delete
 */
function deleteImageWithRetry (config, auth, imageId) {
  return withTransientRetry(() =>
    deleteImage({
      image: Map({ id: imageId }),
      auth,
      server: getConfigDataServer(config),
      processImage: true,
    }),
  )
}

/**
 * Delete one image updating application property by the application key
 * @param {Object} param0 Witness params
 */
export function onDeleteImage ({ after }) {
  return after(DELETE_IMAGE, async ({ action, dispatch, state }) => {
    const { config, auth, data } = state
    const { payload: { storeKey, imageApplicationKey, applicationKey, imageId, imageView } = {} } =
      action
    let { payload: { imageApplicationData } = {} } = action
    if (!imageApplicationData.isEmpty()) {
      imageApplicationData = imageApplicationData.set('deleting', true)
      dispatch(
        setDataApplicationByKey({
          storeKey,
          applicationKey,
          applicationData: Map({ [imageApplicationKey]: imageApplicationData }),
        }),
      )
    }

    try {
      await deleteImageWithRetry(config, auth, imageId)
      // Set the image content references
      const records = getViewRecords({
        data,
        storeKey: STORE_KEY_IMAGES,
        options: imageView.get('options'),
      }).filter((_, k) => k !== imageId)
      dispatch(
        setDataList({
          storeKey: STORE_KEY_IMAGES,
          options: imageView.get('options'),
          error: null,
          total: records.size,
          records,
        }),
      )
    } catch (e) {
      // Do nothing
    }
    if (!imageApplicationData.isEmpty()) {
      imageApplicationData = imageApplicationData.set('deleting', false)
      dispatch(
        setDataApplicationByKey({
          storeKey,
          applicationKey,
          applicationData: Map({ [imageApplicationKey]: imageApplicationData }),
        }),
      )
    }
  })
}

/**
 * Create one image updating application property by the application key
 * @param {Object} param0 Witness params
 */
export function onCreateImage ({ after }) {
  return after(CREATE_IMAGE, async ({ action, dispatch, state }) => {
    const { config, auth } = state
    const { payload: { storeKey, imageApplicationKey, applicationKey } = {} } = action
    let { payload: { imageApplicationData } = Map() } = action
    imageApplicationData = imageApplicationData.set('creating', true)
    dispatch(
      setDataApplicationByKey({
        storeKey,
        applicationKey,
        applicationData: Map({ [imageApplicationKey]: imageApplicationData }),
      }),
    )

    /**
     * Create an image, retrying transient failures
     * @param {Map} image The image to create
     */
    function createImageWithRetry (image) {
      return withTransientRetry(() =>
        createImage({
          image,
          auth,
          server: getConfigDataServer(config),
          processImage: true,
        }),
      )
    }
    try {
      let contentRelationship = imageApplicationData.getIn(['data', 'contentRelationship'])
      // Only create the image if we don't have a content relationship or it is valid
      if (!contentRelationship || isValidImageContentRelationship(contentRelationship)) {
        const image = await createImageWithRetry(imageApplicationData.get('data'))
        contentRelationship = createImageContentRelationshipUnique(contentRelationship)
        // REplace the image data with the API response
        imageApplicationData = imageApplicationData.set('data', image)
        // Set the image in the images store
        dispatch(
          setDataList({
            storeKey: STORE_KEY_IMAGES,
            options: contentRelationship,
            error: null,
            total: 1,
            records: createViewRecords(List([image])),
          }),
        )
      }
    } catch (e) {
      // Do nothing
    }
    dispatch(
      setDataApplicationByKey({
        storeKey,
        applicationKey,
        applicationData: Map({
          [imageApplicationKey]: imageApplicationData.set('creating', false),
        }),
      }),
    )
  })
}
